import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Polygon, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Icon from '../../../components/AppIcon';

// Fix default marker icon issue in Leaflet
delete L?.Icon?.Default?.prototype?._getIconUrl;
L?.Icon?.Default?.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Component to fit map bounds to polygon
const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds?.length > 0) {
      map?.fitBounds(bounds);
    }
  }, [bounds, map]);
  return null;
};

const FarmMap = ({ farmData, language, country }) => {
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMapLoaded(true), 300);
    return () => clearTimeout(timer);
  }, []);

  if (!farmData) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <p className="text-muted-foreground text-center">
          {language === 'en' ? 'Farm location data not available' : '农场位置数据不可用'}
        </p>
      </div>
    );
  }

  const latitude = parseFloat(farmData?.centroidLat) || 0;
  const longitude = parseFloat(farmData?.centroidLng) || 0;
  
  // Parse GeoJSON polygon coordinates if available
  let polygonCoordinates = [];
  let leafletPolygonCoords = [];
  if (farmData?.geojson) {
    try {
      const geojson = typeof farmData?.geojson === 'string' ? JSON.parse(farmData?.geojson) : farmData?.geojson;
      if (geojson?.type === 'Polygon' && geojson?.coordinates?.[0]) {
        polygonCoordinates = geojson?.coordinates?.[0];
      } else if (geojson?.type === 'Feature' && geojson?.geometry?.type === 'Polygon') {
        polygonCoordinates = geojson?.geometry?.coordinates?.[0];
      }
      
      // Convert to Leaflet format [lat, lng]
      leafletPolygonCoords = polygonCoordinates?.map(coord => [coord?.[1], coord?.[0]]);
    } catch (error) {
      console.error('Failed to parse GeoJSON:', error);
    }
  }

  const calculateArea = () => {
    if (polygonCoordinates?.length < 3) return null;
    let area = 0;
    for (let i = 0; i < polygonCoordinates?.length - 1; i++) {
      const [x1, y1] = polygonCoordinates?.[i];
      const [x2, y2] = polygonCoordinates?.[i + 1];
      area += (x1 * y2) - (x2 * y1);
    }
    return Math.abs(area / 2) * 111 * 111;
  };

  const farmArea = calculateArea();

  // Custom coffee-colored marker icon
  const coffeeIcon = new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
        <path fill="#D2691E" stroke="#FFFFFF" stroke-width="2" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.4 12.5 28.5 12.5 28.5S25 20.9 25 12.5C25 5.6 19.4 0 12.5 0z"/>
        <circle cx="12.5" cy="12.5" r="6" fill="#8B4513"/>
      </svg>
    `),
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-heading font-semibold coffee-text-dark">
          {language === 'en' ? 'Farm Location & Boundary' : '农场位置与边界'}
        </h2>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Mountain" size={16} />
          <span>{farmData?.altitudeM}m {language === 'en' ? 'elevation' : '海拔'}</span>
        </div>
      </div>
      {!mapLoaded && (
        <div className="flex items-center justify-center py-12 bg-muted rounded-lg" style={{ height: '400px' }}>
          <div className="text-center">
            <Icon name="Loader" size={40} className="animate-spin text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {language === 'en' ? 'Loading farm map...' : '加载农场地图中...'}
            </p>
          </div>
        </div>
      )}
      {/* Leaflet Map Container */}
      {mapLoaded && latitude && longitude && (
        <div className="rounded-lg overflow-hidden border border-border" style={{ height: '400px', width: '100%' }}>
          <MapContainer
            center={[latitude, longitude]}
            zoom={leafletPolygonCoords?.length > 0 ? 13 : 15}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* Farm Boundary Polygon */}
            {leafletPolygonCoords?.length > 0 && (
              <>
                <Polygon
                  positions={leafletPolygonCoords}
                  pathOptions={{
                    color: '#D2691E',
                    weight: 3,
                    opacity: 0.9,
                    fillColor: '#8B4513',
                    fillOpacity: 0.35,
                  }}
                >
                  <Popup>
                    <div style={{ padding: '8px', fontFamily: 'system-ui' }}>
                      <h3 style={{ margin: '0 0 8px 0', color: '#8B4513', fontSize: '16px', fontWeight: '600' }}>
                        {language === 'en' ? farmData?.nameEn : farmData?.nameZh}
                      </h3>
                      <p style={{ margin: '4px 0', color: '#666', fontSize: '14px' }}>
                        <strong>{language === 'en' ? 'Region:' : '地区：'}</strong> {farmData?.region}
                      </p>
                      <p style={{ margin: '4px 0', color: '#666', fontSize: '14px' }}>
                        <strong>{language === 'en' ? 'Altitude:' : '海拔：'}</strong> {farmData?.altitudeM}m
                      </p>
                      {farmData?.farmSizeHa && (
                        <p style={{ margin: '4px 0', color: '#666', fontSize: '14px' }}>
                          <strong>{language === 'en' ? 'Size:' : '面积：'}</strong> {farmData?.farmSizeHa} ha
                        </p>
                      )}
                    </div>
                  </Popup>
                </Polygon>
                <FitBounds bounds={leafletPolygonCoords} />
              </>
            )}
            
            {/* Center Marker */}
            <Marker position={[latitude, longitude]} icon={coffeeIcon}>
              <Popup>
                <div style={{ padding: '4px' }}>
                  <strong>{language === 'en' ? farmData?.nameEn : farmData?.nameZh}</strong>
                  <br />
                  <small>{farmData?.region}</small>
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        </div>
      )}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="MapPin" size={20} color="var(--color-primary)" />
            <div>
              <p className="font-medium mb-1">{language === 'en' ? farmData?.nameEn : farmData?.nameZh}</p>
              <p className="text-sm text-muted-foreground">{farmData?.region}, {language === 'en' ? country?.nameEn : country?.nameZh}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {language === 'en' ? 'Coordinates' : '坐标'}: {latitude?.toFixed(4)}, {longitude?.toFixed(4)}
              </p>
            </div>
          </div>
        </div>

        {polygonCoordinates?.length > 0 && (
          <div className="p-4 rounded-lg border-2 border-[#D2691E] bg-[#8B4513]/10">
            <div className="flex items-start gap-3">
              <Icon name="Maximize2" size={20} className="coffee-text-medium" />
              <div>
                <p className="font-medium mb-1 coffee-text-dark">
                  {language === 'en' ? 'Farm Boundary (Shapefile)' : '农场边界（形状文件）'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {language === 'en' ? 'Polygon Area' : '多边形区域'}: {polygonCoordinates?.length} {language === 'en' ? 'points' : '点'}
                </p>
                {farmArea && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {language === 'en' ? 'Calculated Area' : '计算面积'}: {farmArea?.toFixed(2)} ha
                  </p>
                )}
                <p className="text-xs coffee-text-medium mt-2 flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#8B4513', opacity: 0.35, border: '2px solid #D2691E' }}></span>
                  {language === 'en' ? 'Coffee color boundary' : '咖啡色边界'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FarmMap;